import React, { Fragment } from 'react';
import { View, App } from '@gin-betting/core';
import type { Betslip } from '@gin-betting/betslip';

import { ParentPath } from 'types';
import MatchTime from '../MatchTime';
import SmartLink from '../SmartLink';
import CompetitorLogo from '../CompetitorLogo';
import LiveLabel from '../LiveLabel';
import DateTime from '../DateTime';
import PromoMatchOdds from '../PromoMatchOdds';
import PromoMatchDynamicScoreList from '../PromoMatchDynamicScoreList';

import './promoMatchBody.scss';

interface Props {
    matchId: string;
    sportId: string;
    linkToMatch: string;
    tournament: Pick<View.Tournament, 'id' | 'name' | 'slug'> | null;
    competitors: View.CompetitorsValid;
    oddIds?: string[];
    dateAndTime: string;
    marketName: string;
    isLive: boolean;
    OddButton: Betslip.OddButtonComponent;
    withMatchLink?: boolean;
    liveLabelVisibility?: 'visible' | 'faded';
    showDynamicScores?: boolean;
    route: ParentPath;
}

const PromoMatchBody: React.FC<Props> = ({
    matchId,
    linkToMatch,
    oddIds,
    tournament,
    competitors,
    dateAndTime,
    marketName,
    isLive,
    liveLabelVisibility,
    withMatchLink = true,
    OddButton,
    showDynamicScores = false,
    sportId,
    route,
}) => {
    const CompetitorItemChild = (
        <Fragment>
            <div
                styleName="competitor-item"
                className="__app-PromoMatchBody-competitor-item __app-PromoMatchBody-competitor-item--home"
            >
                <CompetitorLogo
                    logoUrl={competitors.homeLogo}
                    sportId={sportId}
                />
                <div
                    styleName="competitor-name"
                    className="__app-PromoMatchBody-competitor-name"
                >
                    {competitors.homeName}
                </div>
            </div>

            <div
                styleName="info-container"
                className="__app-PromoMatchBody-info-container"
            >
                {liveLabelVisibility && (
                    <div
                        className="__app-PromoMatchBody-live-container"
                        styleName="live-container"
                    >
                        <LiveLabel isFaded={liveLabelVisibility === 'faded'} />

                        {liveLabelVisibility === 'visible' && (
                            <MatchTime matchId={matchId} sportId={sportId} />
                        )}
                    </div>
                )}

                {isLive ? (
                    <PromoMatchDynamicScoreList
                        matchId={matchId}
                        sportId={sportId}
                        showDynamicScores={showDynamicScores}
                    />
                ) : (
                    <DateTime startTime={dateAndTime} isLiveMatch={false} />
                )}
            </div>

            <div
                styleName="competitor-item"
                className="__app-PromoMatchBody-competitor-item __app-PromoMatchBody-competitor-item--away"
            >
                <CompetitorLogo
                    logoUrl={competitors.awayLogo}
                    sportId={sportId}
                />
                <div
                    styleName="competitor-name"
                    className="__app-PromoMatchBody-competitor-name"
                >
                    {competitors.awayName}
                </div>
            </div>
        </Fragment>
    );

    return (
        <div className="__app-PromoMatchBody-container" styleName="container">
            {tournament && (
                <div
                    styleName="tournament"
                    className="__app-PromoMatchBody-tournament"
                >
                    <SmartLink
                        to={`${route}${App.Routes.TOURNAMENT}${tournament.slug}`}
                    >
                        {tournament.name}
                    </SmartLink>
                </div>
            )}

            <div
                styleName="competitors"
                className="__app-PromoMatchBody-competitors"
            >
                {withMatchLink ? (
                    <SmartLink to={linkToMatch}>
                        {CompetitorItemChild}
                    </SmartLink>
                ) : (
                    CompetitorItemChild
                )}
            </div>

            <div
                styleName="market-name"
                className="__app-PromoMatchBody-market-name"
            >
                {marketName || <span styleName="stub">-</span>}
            </div>

            <PromoMatchOdds
                oddIds={oddIds}
                OddButton={OddButton}
                competitors={competitors}
            />
        </div>
    );
};

export default React.memo(PromoMatchBody);
